import { ProductHistory, isNullish, theme } from '@arnold/common';
import { ProductCode, getLocale, getLocalizedDateWithoutLeadingZeroes, prepareSurveyDates } from '@arnold/core';
import styled from '@emotion/styled';
import { differenceInDays, format, startOfDay } from 'date-fns';
import { FC } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { SurveyGroupRespondentsQuery, SurveyGroupsQuery, SurveyStatus } from '../../generated/hooks';
import { getTGTranslation } from '../../lib/helpers/surveyGroup';
import { prepareStepOffsets } from '../../lib/process';
import { Table } from '../Table/Table';

type SurveyGroupSurveysTableProps = {
  topicGroup: NonNullable<SurveyGroupsQuery['organization']>['surveyGroups'][0]['topicGroup'];
  alreadyStartedSurveys?: NonNullable<
    NonNullable<SurveyGroupRespondentsQuery['surveyGroupRespondents']['data']>[0]
  >['surveys'];
  addedOn: Date | null;
  productHistory?: ProductHistory[] | null;
};

const StyledWrapper = styled.div`
  display: contents !important;
  & td {
    color: ${theme.colors.text.disabled} !important;
  }
`;

type TableDataItem = {
  id: string;
  topic: string;
  daysFromStart: string;
  startDate: string;
  status: string;
  foundStartedSurvey: boolean;
  rawStatus?: SurveyStatus;
  willNotBeSent: boolean;
};

export const getDataSource = (
  topicGroup: NonNullable<SurveyGroupsQuery['organization']>['surveyGroups'][0]['topicGroup'],
  addedOn: Date | null,
  language: string,
  t: TFunction,
  productHistory?: ProductHistory[] | null,
  alreadyStartedSurveys?: NonNullable<
    NonNullable<SurveyGroupRespondentsQuery['surveyGroupRespondents']['data']>[0]
  >['surveys'],
): TableDataItem[] =>
  topicGroup.steps
    ?.map((step) => {
      const offsets = prepareStepOffsets({
        startOffsetMins: step.startOffsetMins,
        endOffsetMins: step.endOffsetMins,
        remindersOffsetMins: step.reminders.map((reminder) => reminder.reminderOffsetMins),
      });
      if (isNullish(offsets) || isNullish(offsets.startOffset?.days)) {
        return null;
      }
      const diffInDaysFromStep = offsets.startOffset!.days;
      const basicDescription = {
        id: step.id,
        status: '-',
        rawStatus: undefined,
        topic: getTGTranslation(step.stepTopicGroup.translations, language)!.value || '',
        startDate: '-',
        daysFromStart: t('processDetail:dayText', {
          dayNo: diffInDaysFromStep + (diffInDaysFromStep >= 0 ? 1 : 0),
        }),
      };
      if (!addedOn) {
        return basicDescription;
      }
      const groupAddedOn = startOfDay(addedOn);
      const surveyDate = prepareSurveyDates(step, groupAddedOn, true);
      if (!surveyDate) {
        return basicDescription!;
      }
      const surveyStart = surveyDate.sendAt;
      const now = new Date();
      const diffInDays = differenceInDays(startOfDay(surveyDate.sendAt), startOfDay(groupAddedOn));
      const status =
        surveyDate.endsAt < now
          ? SurveyStatus.Completed
          : surveyDate.sendAt < now
            ? SurveyStatus.Ongoing
            : SurveyStatus.Prepared;

      const activeProductAtAddedOn = productHistory?.find(
        (ph) =>
          new Date(ph.validFrom).getTime() <= addedOn.getTime() && new Date(ph.validTo).getTime() >= addedOn.getTime(),
      );
      const activeProductAtTheSurveyStart = productHistory?.find(
        (ph) =>
          new Date(ph.validFrom).getTime() <= surveyStart.getTime() &&
          new Date(ph.validTo).getTime() >= surveyStart.getTime(),
      );

      const willNotBeSent =
        (!activeProductAtAddedOn && !activeProductAtTheSurveyStart) ||
        // If the product is free and topic has org id, that means that it is a custom topic and the survey will not be sent
        ((!activeProductAtTheSurveyStart || activeProductAtTheSurveyStart?.product === ProductCode.Free) &&
          topicGroup.organization?.id !== undefined);
      const foundStartedSurvey = alreadyStartedSurveys?.find((s) => s.topicGroup?.id === step.stepTopicGroup.id);

      const realSurveyStart = foundStartedSurvey?.sendAt ? new Date(foundStartedSurvey?.sendAt) : surveyStart;
      const realStatus = foundStartedSurvey?.status ?? status;
      return {
        ...basicDescription,
        foundStartedSurvey: !!foundStartedSurvey,
        rawStatus: realStatus,
        willNotBeSent,
        status: t(
          `survey:status-${
            (!foundStartedSurvey && realStatus === SurveyStatus.Completed) || willNotBeSent ? 'NOTCREATED' : realStatus
          }`,
        ),
        startDate: `${getLocalizedDateWithoutLeadingZeroes(realSurveyStart, language)} ${format(realSurveyStart, 'p', {
          locale: getLocale(language),
        })}`,
        daysFromStart: t('processDetail:dayText', {
          dayNo: diffInDays + (diffInDays >= 0 ? 1 : 0),
        }),
      };
    })
    .filter((item): item is TableDataItem => !!item) ?? [];

export const SurveyGroupSurveysPreviewTable: FC<SurveyGroupSurveysTableProps> = ({
  topicGroup,
  addedOn,
  productHistory,
  alreadyStartedSurveys,
}) => {
  const { i18n, t } = useTranslation('surveyGroup');

  return (
    <Table<TableDataItem>
      thin={true}
      className="mt-8"
      columns={[
        {
          key: 'topic',
          title: t('topic'),
          dataIndex: 'topic',
          width: '25%',
        },
        {
          key: 'daysFromStart',
          title: t('SequenceSteps:startOffset'),
          dataIndex: 'daysFromStart',
          width: '25%',
        },
        {
          key: 'startDate',
          title: t('surveyStartDate'),
          dataIndex: 'startDate',
          width: '25%',
        },
        {
          key: 'status',
          title: t('surveyGroupStatus'),
          dataIndex: 'status',
          width: '25%',
        },
      ]}
      wrapRow={(data, component) => {
        return <>{data.foundStartedSurvey ? <StyledWrapper>{component}</StyledWrapper> : component}</>;
      }}
      dataSources={getDataSource(topicGroup, addedOn, i18n.language, t, productHistory, alreadyStartedSurveys)}
      selected={undefined}
    />
  );
};
